import React, { useState, useEffect } from 'react';
import {
  AccountSettingsRoot,
  AS_ACCESS_CONTAINER,
  AS_FLEX_ROW_CLASS,
  AS_FORM_STYLE,
  AS_LOC_ICON,
  AS_SCREEN_CONTAINER,
  AS_SCREEN_CONTAINER_MOBILE,
  AS_SCREEN_WIDTH,
  ButtonContainer,
  FullWidth,
  LocRow,
} from './styles';
import Form from 'global/components/form';
import { get, size, set, isEqual, orderBy } from 'global/lib/lodash';
import { Paper, Icon, LinearProgress, InputLabel, FormHelperText, Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import sendData from 'redux/actions/sendData';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useData } from 'global/hooks/useData';
import clsx from 'clsx';
import { CommunityIcon, DivisionIcon } from 'global/components/PerformIcons';
import { FullScreen } from 'global/components/modal';
import Droprealms, { Access } from 'global/components/droprealms';
import { allRealmsForUserRealm, getBase64Image, parseJson, toBase64, getUserAppPermissions, appPermissionToServer, _size } from 'global/utils';
import { MAXSIZE } from 'global/components/drop-area';
import { getViewStyle, setViewStyle } from 'redux/actions/view';
import PermissionGate from 'global/components/permission-gate';
import { getUserPermissions } from 'redux/actions/userLogin';
import { SET_ACCOUNT_SETTINGS } from 'redux/actions/types';
import useNavSettings from 'global/hooks/useNavSettings';
import { useUser } from 'global/hooks/useUser';
import { useScreen } from 'global/styles';
import { Text } from '../../../global/components/text';
import { Row } from '../../../global/components/row';
import { Button, IconButton } from '../../../global/components/Button';
import { UserGroupModal } from '../user-group';
import ConfirmModal from '../../../global/components/confirm';
import PrintOptionsEditor from './PrintOptionsEditor';

export const SETTINGS = {
  logo: 'Logo',
  primaryColor: 'Primary Color',
  secondaryColor: 'Secondary Color',
  selfRegistration: 'Self Registration',
  accessRestrictions: 'Self Registration Access Restrictions',
  allowedDomains: 'Self Registration Allowed Domains',
  assessmentAccess: 'Assessment Default General Access',
  assessmentScorecard: 'Assessment Default Scorecard',
  assessmentColorScale: 'Assessment Default Color Scale',
  defaultPermissions: 'Builder Default Permissions',
  taskDefault: 'Builder Task Settings',
  assessmentMetaDefaults: 'Assessment Meta Defaults',
  customAssessmentsAllowed: 'Allow Custom Assessments',
  assessmentPrintOptions: 'Assessment Print Options',
  appMode: 'App Mode',
  // PFM-2646: ALL link-sharing config lives in this single backend row,
  // value is a JSON blob: { default: { enabled, expiration_days, allow_mode, allowed_domains } }.
  linkSharing: 'Link Sharing',
  // PFM-2547 / PFM-2564: these were phantom backend names that never existed
  // server-side (reads silently defaulted, writes 500'd). Kept only so any
  // stale references don't break the build; do NOT use them as setting names.
  shareLinkAllowMode: 'Share Link Allow Mode',
  shareLinkDefaultExpirationDays: 'Share Link Default Expiration Days',
  shareLinkDefaultAccessScope: 'Share Link Default Access Scope',
  shareLinkAllowedDomains: 'Share Link Allowed Domains',
};

export const SHARE_LINK_ACCESS_SCOPES = [
  { value: 'registered-users', title: 'Registered Users Only' },
  { value: 'email-list', title: 'Specific Email List' },
  { value: 'anyone-with-link', title: 'Anyone With the Link' },
];

// PFM-2564: builder-wide policy for the "Anyone with the link can view" checkbox.
//   allowed-default-on  — checkbox shown, defaulted ON
//   allowed-default-off — checkbox shown, defaulted OFF
//   not-allowed         — checkbox hidden, live links cannot be created
export const SHARE_LINK_ALLOW_MODES = [
  { value: 'allowed-default-on', title: 'Live Link Sharing Allowed (on by default)' },
  { value: 'allowed-default-off', title: 'Live Link Sharing Allowed (off by default)' },
  { value: 'not-allowed', title: 'Live Link Sharing Not Allowed' },
];

export const APP_MODE_IDS = { Quality: 1, Safety: 2 };

const COLORKEYS = [SETTINGS.primaryColor, SETTINGS.secondaryColor];

export const taskOptions = (opts = {}) => {
  opts = {
    addTo: (r) => r,
    ...opts,
  };
  return [
    { label: 'Notes required to close', value: 'notes_required' },
    { label: 'Photos required to close', value: 'photos_required' },
  ].map((o) => opts.addTo(o));
};

export default PermissionGate((props) => {
  const [canEdit, setCanEdit] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [hideForm, setHideForm] = useState(false);
  const [formState, setFormState] = useState({});
  const [error, setError] = useState();
  const { me } = useUser();
  const { isMobile } = useScreen();
  const [accessModal, setAccessModal] = useState(false);
  const [allRealms, setAllRealms] = useState([]);
  const [settings, setSettings] = useState({});
  const dispatch = useDispatch();
  const [ac, setAc] = useState({});
  const { filterPermissionsByNav } = useNavSettings();
  const [ugmodal, setugmodal] = useState({
    open: false,
    onClose: () => {
      setugmodal((s) => ({ ...s, open: false }));
    },
  });
  const [conf, setconf] = useState({});
  const { destroyCache } = useData();

  const aSettings = useRetrieveData({ url: `/account-settings` }, { build: ['accountSettings'] });
  const { data, loading, update } = useRetrieveData({ url: `/realms?realmPicker=1` }, { build: ['realms'] });
  const scorecards = useRetrieveData({ url: '/scorecards' }, { build: ['scorecards'], store: 'data.scorecards' });
  const usergroups = useRetrieveData(
    { url: `/user-groups` },
    {
      build: ['userGroups'],
      store: 'data.usergroups',
      params: { include: 'users' },
      transformData: (d) => {
        return orderBy(d, 'name');
      },
    }
  );

  const userRealm = get(me, 'realm');

  useEffect(() => {
    (async () => {
      try {
        if (!aSettings.loading && !loading) {
          setHideForm(true);

          let aRealms = data;
          setAllRealms(allRealmsForUserRealm(data, me.realm));

          const obj = {};
          const toParse = [
            SETTINGS.accessRestrictions,
            SETTINGS.allowedDomains,
            SETTINGS.defaultPermissions,
            SETTINGS.assessmentColorScale,
            SETTINGS.appMode,
            SETTINGS.assessmentMetaDefaults,
            SETTINGS.assessmentPrintOptions,
            SETTINGS.linkSharing,
          ];
          await Promise.all(
            aSettings.data.map(async (setting) => {
              const value = async () => {
                if (COLORKEYS.indexOf(setting.name) > -1) {
                  return setting.value.replace('#', '');
                } else if (setting.name === SETTINGS.logo) {
                  if (setting.value.indexOf('https://perform-3-media-dev.s3.amazonaws.com/local/medialibrary/456291/perform_logo_white.png') > -1) {
                    setting.value = 'https://perform-3-media-dev.s3.amazonaws.com/local/medialibrary/456291/perform_logo_white.png';
                  }
                  const val = await getBase64Image(setting.value);
                  return `data:image/png;base64,${val}`;
                } else if (toParse.indexOf(setting.name) > -1) {
                  return parseJson(setting.value);
                } else {
                  return setting.value;
                }
              };
              const val = await value();
              set(obj, setting.name, val);
            })
          );

          const userPerms = getUserAppPermissions(get(obj, SETTINGS.defaultPermissions, []).map((p) => ({ name: p })));
          const access = {};
          (userPerms || []).map((up) => set(access, up.value, true));
          setAc(access);

          setSettings(obj);
          const realmIds = get(obj, SETTINGS.accessRestrictions);
          if (size(realmIds)) {
            setFormState({
              ...formState,
              [SETTINGS.accessRestrictions]: realmIds.map((id) => aRealms.find((r) => r.id == id)).filter((r) => r),
            });
          }
          setHideForm(false);
        }
      } catch (err) {
        console.error({ err });
        setHideForm(false);
      }
    })();
  }, [aSettings.loading, loading]);

  const primaryColor = get(formState, SETTINGS.primaryColor);
  const secondaryColor = get(formState, SETTINGS.secondaryColor);

  useEffect(() => {
    dispatch(
      setViewStyle({
        primaryColor: primaryColor ? `#${primaryColor}` : undefined,
        secondaryColor: secondaryColor ? `#${secondaryColor}` : undefined,
      })
    );
  }, [primaryColor, secondaryColor]);

  const toggleEdit = () => {
    setCanEdit(!canEdit);
  };

  const formatData = async () => {
    if (!get(formState, 'name')) {
      throw new Error("Name can't be blank");
    }
    const realmData = {
      data: {
        type: 'realms',
        id: get(userRealm, 'id'),
        attributes: {
          name: get(formState, 'name', ''),
        },
      },
    };

    const settingsData = (
      await Promise.all(
        Object.keys(settings).map(async (k) => {
          const origSetting = settings[k];
          const formSetting = get(formState, k);
          const reqSetting = aSettings.data.find((a) => get(a, 'name') === k);

          // Allow logo removal to flow through (formSetting may be []).
          const isLogo = k === SETTINGS.logo;
          const isLogoCleared = isLogo && Array.isArray(formSetting) && formSetting.length === 0 && !!origSetting;
          // Save when the form value is defined (including an empty array, e.g.
          // cleared domains) and differs from the original stored value. The old
          // `&& formSetting` guard dropped empty-array clears. (PFM-2646)
          const formSettingDefined = formSetting !== undefined && formSetting !== null;
          if ((!isEqual(origSetting, formSetting) && formSettingDefined) || isLogoCleared) {
            const _attributes = async () => {
              if (COLORKEYS.indexOf(k) > -1) {
                return {
                  value: `#${formSetting}`,
                };
              } else if (k === SETTINGS.logo) {
                if (isLogoCleared) {
                  return { value: '' };
                }
                const val = get(formSetting, '0');
                const upload = await toBase64(val);

                if (upload === get(settings, k)) {
                  //dont update the image if it never changed
                  return null;
                }

                return {
                  value: `${get(val, 'name', '').replace('.png')}.png`,
                  upload,
                };
              } else if (k === SETTINGS.accessRestrictions) {
                return {
                  value: JSON.stringify((formSetting || []).filter((f) => f).map((r) => r.id)),
                };
              } else if ([SETTINGS.allowedDomains, SETTINGS.taskDefault, SETTINGS.assessmentMetaDefaults, SETTINGS.assessmentPrintOptions].includes(k)) {
                return {
                  value: JSON.stringify(formSetting),
                };
              } else if (k === SETTINGS.defaultPermissions) {
                return {
                  value: JSON.stringify(
                    appPermissionToServer(
                      Object.keys(formSetting)
                        .filter((k) => formSetting[k])
                        .map((k) => k)
                    )
                  ),
                };
              } else if (k === SETTINGS.assessmentColorScale) {
                return { value: JSON.stringify(formSetting) };
              } else {
                return {
                  value: formSetting,
                };
              }
            };

            const attributes = await _attributes();

            if (!attributes) {
              return null;
            }

            const override = get(reqSetting, 'override');
            const account_setting_id = get(reqSetting, 'id', '').toString();
            const overrideId = (get(reqSetting, 'overrideId') || '').toString();
            // Don't emit an unanchored realm-account-setting. A setting with no
            // base account-setting row (account_setting_id === '') cannot be
            // created — the backend 500s on it, and because these requests run
            // under Promise.all a single rejection aborts the entire save
            // (every other setting AND the /realms patch), surfacing a blocking
            // error. (PFM-2646)
            if (!account_setting_id) {
              return null;
            }
            set(attributes, 'account_setting_id', account_setting_id);
            set(attributes, 'realm_id', get(userRealm, 'id', '').toString());
            return {
              data: {
                id: override === 1 ? overrideId : undefined,
                type: 'realm-account-settings',
                attributes,
              },
            };
          }
        })
      )
    ).filter((f) => f);

    // Handle App Mode: combine quality/safety toggles into the App Mode array
    const qualityFormVal = get(formState, '_qualityMode');
    const safetyFormVal = get(formState, '_safetyMode');
    if (qualityFormVal !== undefined || safetyFormVal !== undefined) {
      // Use the LAST "App Mode" entry – the API may return multiple entries
      // (base + realm override) and the last one is the most specific.
      const appModeSetting = [...aSettings.data].reverse().find((a) => get(a, 'name') === SETTINGS.appMode);
      if (appModeSetting) {
        const origArr = get(settings, SETTINGS.appMode, [APP_MODE_IDS.Quality]);
        const safeOrigArr = Array.isArray(origArr) ? origArr : [APP_MODE_IDS.Quality];
        const qualityOn = String(qualityFormVal !== undefined ? qualityFormVal : safeOrigArr.includes(APP_MODE_IDS.Quality) ? '1' : '0') === '1';
        const safetyOn = String(safetyFormVal !== undefined ? safetyFormVal : safeOrigArr.includes(APP_MODE_IDS.Safety) ? '1' : '0') === '1';
        const newArr = [];
        if (qualityOn) newArr.push(APP_MODE_IDS.Quality);
        if (safetyOn) newArr.push(APP_MODE_IDS.Safety);
        // Always send the update when modes were touched to ensure save goes through
        const override = get(appModeSetting, 'override');
        const account_setting_id = get(appModeSetting, 'id', '').toString();
        const overrideId = (get(appModeSetting, 'overrideId') || '').toString();
        settingsData.push({
          data: {
            id: override === 1 ? overrideId : undefined,
            type: 'realm-account-settings',
            attributes: {
              value: JSON.stringify(newArr),
              account_setting_id,
              realm_id: get(userRealm, 'id', '').toString(),
            },
          },
        });
      }
    }

    // PFM-2646: Handle Link Sharing. ALL share-link policy lives in the single
    // "Link Sharing" backend row as a JSON blob. Mirror the App Mode pattern:
    // locate the row by name (reverse-find for the most specific/override
    // entry), reuse its id/overrideId, and emit ONE realm-account-settings
    // write that merges the touched form values into the existing blob —
    // spreading so any other keys/scopes are preserved.
    const shareAllowMode = get(formState, '_shareAllowMode');
    const shareExpirationDays = get(formState, '_shareExpirationDays');
    const shareAllowedDomains = get(formState, '_shareAllowedDomains');
    const shareTouched = shareAllowMode !== undefined || shareExpirationDays !== undefined || shareAllowedDomains !== undefined;
    if (shareTouched) {
      const linkSharingSetting = [...aSettings.data].reverse().find((a) => get(a, 'name') === SETTINGS.linkSharing);
      if (linkSharingSetting) {
        const existingBlob = (() => {
          const parsed = get(settings, SETTINGS.linkSharing);
          return parsed && typeof parsed === 'object' ? parsed : {};
        })();
        const existingDefault = existingBlob.default && typeof existingBlob.default === 'object' ? existingBlob.default : {};

        // allow_mode: form value if touched, else current, else derive from enabled.
        const allowMode = shareAllowMode !== undefined ? shareAllowMode : typeof existingDefault.allow_mode === 'string' ? existingDefault.allow_mode : existingDefault.enabled ? 'allowed-default-on' : 'not-allowed';
        // enabled is the backend-authoritative gate: 0 only when not-allowed.
        const enabled = allowMode === 'not-allowed' ? 0 : 1;
        // expiration_days kept as a string (1–30). Defense-in-depth: clamp to a
        // whole number in [1,30] so an out-of-range value can never be persisted
        // (formSubmit already blocks save via invalidShareExpirationDays()).
        const rawExpirationDays = shareExpirationDays !== undefined ? shareExpirationDays : existingDefault.expiration_days != null ? existingDefault.expiration_days : '7';
        const clampedExpirationDays = Math.min(30, Math.max(1, Math.round(Number(rawExpirationDays)) || 7));
        const expirationDays = `${clampedExpirationDays}`;
        // allowed_domains: a plain array of strings (same shape as Self
        // Registration Allowed Domains — JSON-stringified array of strings).
        const allowedDomains = shareAllowedDomains !== undefined ? (Array.isArray(shareAllowedDomains) ? shareAllowedDomains : []) : Array.isArray(existingDefault.allowed_domains) ? existingDefault.allowed_domains : [];

        const mergedBlob = {
          ...existingBlob,
          default: {
            ...existingDefault,
            enabled,
            expiration_days: expirationDays,
            allow_mode: allowMode,
            allowed_domains: allowedDomains,
          },
        };

        const override = get(linkSharingSetting, 'override');
        const account_setting_id = get(linkSharingSetting, 'id', '').toString();
        const overrideId = (get(linkSharingSetting, 'overrideId') || '').toString();
        settingsData.push({
          data: {
            id: override === 1 ? overrideId : undefined,
            type: 'realm-account-settings',
            attributes: {
              value: JSON.stringify(mergedBlob),
              account_setting_id,
              realm_id: get(userRealm, 'id', '').toString(),
            },
          },
        });
      }
    }

    await Promise.all(
      (settingsData || []).map(async (req) => {
        const id = get(req, 'data.id');
        const method = id ? 'patch' : 'post';
        await sendData({
          url: `/realm-account-settings${method === 'patch' ? `/${id}` : ''}`,
          method,
          data: req,
        });
      })
    );

    await sendData({
      url: `/realms/${get(userRealm, 'id')}`,
      method: 'patch',
      data: realmData,
    });
  };

  const reload = async () => {
    setFormState({});
    destroyCache(['account-settings']);
    const freshSettings = await aSettings.update();
    await Promise.all([await update(), await dispatch(getViewStyle()), await dispatch(getUserPermissions())]);
    if (_size(freshSettings)) {
      dispatch({ type: SET_ACCOUNT_SETTINGS, accountSettings: freshSettings });
    }
  };

  const bothModesOff = () => {
    const arr = get(settings, SETTINGS.appMode, [APP_MODE_IDS.Quality]);
    const safeArr = Array.isArray(arr) ? arr : [APP_MODE_IDS.Quality];
    const qVal = get(formState, '_qualityMode');
    const sVal = get(formState, '_safetyMode');
    const qualityOn = String(qVal !== undefined ? qVal : safeArr.includes(APP_MODE_IDS.Quality) ? '1' : '0') === '1';
    const safetyOn = String(sVal !== undefined ? sVal : safeArr.includes(APP_MODE_IDS.Safety) ? '1' : '0') === '1';
    return !qualityOn && !safetyOn;
  };

  // Validates the effective Days Link Active value (form value if touched,
  // else current/default). Returns an error message string when invalid, or
  // undefined when valid. Value must be a whole number between 1 and 30.
  const invalidShareExpirationDays = () => {
    const formVal = get(formState, '_shareExpirationDays');
    const effective = formVal !== undefined ? formVal : shareExpirationDaysDefault();
    const num = Number(effective);
    if (!Number.isInteger(num) || num < 1 || num > 30) {
      return 'Days Link Active must be a whole number between 1 and 30.';
    }
    return undefined;
  };

  const formSubmit = async () => {
    try {
      setError();
      if (bothModesOff()) {
        setError('At least one mode must be turned on.');
        return;
      }
      const shareExpirationError = invalidShareExpirationDays();
      if (shareExpirationError) {
        setError(shareExpirationError);
        return;
      }
      setFormLoading(true);
      await formatData();
      setCanEdit(false); //this forces reload on the canEdit effect
      setFormLoading(false);
    } catch (err) {
      setFormLoading(false);
      setError(`${err.message}. Please try again.`);
    }
  };

  const onFormState = (vals) => {
    setFormState({ ...formState, ...vals });
  };

  const reset = async () => {
    setHideForm(true);
    await reload();
    setHideForm(false);
  };

  useEffect(() => {
    if (!canEdit && !loading) {
      reset();
    }
  }, [canEdit]);

  const onUpdateAccess = (realms) => {
    const resRealms = Object.keys(realms).map((k) => realms[k]);
    setFormState({
      ...formState,
      [SETTINGS.accessRestrictions]: resRealms,
    });
  };

  const toggleAccess = () => {
    setAccessModal(!accessModal);
  };

  const divisions = allRealms.filter((r) => get(r, 'tier') === 'division');
  const communitys = allRealms.filter((r) => get(r, 'tier') === 'community');

  const renderLocations = () => {
    return (
      <div>
        <LocRow>
          <DivisionIcon className={AS_LOC_ICON} />
          <Text>{`${size(divisions)} Divisions`}</Text>
        </LocRow>
        <LocRow>
          <CommunityIcon className={AS_LOC_ICON} />
          <Text>{`${size(communitys)} Communities`}</Text>
        </LocRow>

        {canEdit && (
          <Button variant="contained" startIcon={<Icon>location_on</Icon>} onClick={toggleAccess} color={'secondary'}>
            MANAGE
          </Button>
        )}
      </div>
    );
  };

  const deleteUg = async (ug) => {
    const close = () => {
      setconf({});
    };

    setconf({
      title: (
        <Text type="title_medium">
          Are you sure you want to delete user group <b>{ug.name}</b>?
        </Text>
      ),
      content: 'Deleting this group will not delete any users, but will remove all user group settings and user assignments to this group. It can not be restored.',
      onOk: async () => {
        try {
          await sendData({
            url: `/user-groups/${ug.id}`,
            method: 'delete',
            data: {
              data: {
                type: 'user-groups',
                id: ug.id,
              },
            },
          });
          await usergroups.update();
          close();
        } catch (err) {
          alert(err);
        }
      },
      onCancel: close,
    });
  };

  const addUg = () => {
    setugmodal((s) => ({
      ...s,
      open: true,
      onSubmit: async (vals) => {
        try {
          await sendData({
            url: '/user-groups',
            method: 'post',
            data: {
              data: {
                type: 'user-groups',
                attributes: {
                  ...vals,
                  realm_id: `${userRealm.id}`,
                },
              },
            },
          });
          s.onClose();
          usergroups.update();
        } catch (err) {
          console.log(err);
          alert(err);
        }
      },
    }));
  };

  const renderUserGroups = () => {
    return (
      <div style={{ width: '100%' }}>
        {usergroups.data.map((ug) => {
          return (
            <Row key={ug.id} fullWidth style={{ marginBottom: 5 }}>
              <Icon style={{ marginRight: 5 }}>supervised_user_circle</Icon>
              <Text>{`${ug.name} (${size(ug.users)} users)`}</Text>
              {canEdit && <IconButton style={{ marginLeft: 'auto' }} onClick={() => deleteUg(ug)} size="small" icon="delete" color="secondary" />}
            </Row>
          );
        })}

        {canEdit && (
          <Button variant="contained" startIcon={<Icon>supervised_user_circle</Icon>} onClick={addUg} color={'secondary'}>
            ADD USER GROUP
          </Button>
        )}
      </div>
    );
  };

  const accessRestrictions = get(formState, SETTINGS.accessRestrictions, get(settings, SETTINGS.accessRestrictions, []));

  // PFM-2646: derive the Link Sharing form defaults from the single backend
  // blob's `default` sub-object. `settings[linkSharing]` is the parsed JSON
  // (see toParse). Guard for a missing/blank row.
  const linkSharingDefault = () => {
    const blob = get(settings, SETTINGS.linkSharing);
    return blob && typeof blob === 'object' ? blob.default || {} : {};
  };
  const shareAllowModeDefault = () => {
    const cfg = linkSharingDefault();
    if (typeof cfg.allow_mode === 'string') return cfg.allow_mode;
    return cfg.enabled ? 'allowed-default-on' : 'not-allowed';
  };
  const shareExpirationDaysDefault = () => {
    const cfg = linkSharingDefault();
    return cfg.expiration_days != null ? `${cfg.expiration_days}` : '7';
  };

  return aSettings.loading || loading || hideForm || !_size(settings) ? (
    <LinearProgress />
  ) : (
    <AccountSettingsRoot>
      {error && (
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              size="small"
              onClick={() => {
                setError();
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      <ButtonContainer>
        {!formLoading && (
          <Button color={'secondary'} variant="contained" onClick={toggleEdit} startIcon={<Icon>{canEdit ? 'close' : 'edit'}</Icon>}>
            {'Edit'}
          </Button>
        )}
      </ButtonContainer>

      <Form
        loading={formLoading}
        showAllScreens={true}
        formStyle={AS_FORM_STYLE}
        onSubmit={formSubmit}
        canSubmit={canEdit}
        onFormState={onFormState}
        submitText="Save Changes"
        screens={[
          [
            {
              title: 'General Info',
              screenContainerStyle: isMobile ? AS_SCREEN_CONTAINER_MOBILE : AS_SCREEN_CONTAINER,
              screenContainer: {
                props: {
                  className: AS_SCREEN_WIDTH,
                },
              },
              inputs: [
                {
                  id: 'name',
                  type: 'TextField',
                  defaultValue: () => get(userRealm, 'name', ''),
                  props: {
                    label: 'Name',
                    disabled: !canEdit,
                  },
                },
                {
                  id: SETTINGS.logo,
                  type: 'DropZone',
                  props: {
                    label: 'Logo',
                    disabled: !canEdit,
                    filesLimit: 1,
                    initialFiles: get(settings, SETTINGS.logo) ? [get(settings, SETTINGS.logo)] : [],
                    acceptedFiles: ['image/*'],
                    maxFileSize: MAXSIZE,
                    helperText: !!canEdit && 'Drag and drop an image file here or click container',
                    onDeleteFile: () => {
                      // Mark logo for removal by clearing both the stored
                      // setting (so the initial preview disappears) and the
                      // form value (so formatData picks up the change).
                      setSettings((prev) => ({ ...prev, [SETTINGS.logo]: '' }));
                      setFormState((prev) => ({ ...prev, [SETTINGS.logo]: [] }));
                    },
                  },
                },
                {
                  id: SETTINGS.primaryColor,
                  type: 'ColorSquare',
                  defaultValue: () => get(settings, SETTINGS.primaryColor, ''),
                  props: {
                    label: 'Primary Color',
                    disabled: !canEdit,
                  },
                },
                {
                  id: SETTINGS.secondaryColor,
                  type: 'ColorSquare',
                  defaultValue: () => get(settings, SETTINGS.secondaryColor, ''),
                  props: {
                    label: 'Secondary Color',
                    disabled: !canEdit,
                  },
                },
              ],
            },
            {
              title: 'Assessment Defaults',
              screenContainerStyle: isMobile ? AS_SCREEN_CONTAINER_MOBILE : AS_SCREEN_CONTAINER,
              screenContainer: {
                component: Paper,
                props: {
                  elevation: 5,
                  className: clsx(AS_SCREEN_WIDTH, AS_ACCESS_CONTAINER),
                },
              },
              inputs: [
                {
                  id: SETTINGS.assessmentAccess,
                  type: 'ToggleButton',
                  options: [
                    { title: 'NORMAL', value: 'normal' },
                    { title: 'GLOBAL', value: 'global' },
                    { title: 'HIDDEN', value: 'hidden' },
                  ],
                  defaultValue: () => get(settings, SETTINGS.assessmentAccess, 'normal'),
                  props: {
                    exclusive: true,
                    label: 'General Visibility',
                    disabled: !canEdit,
                  },
                },
                {
                  id: SETTINGS.assessmentScorecard,
                  type: 'Select',
                  defaultValue: () => get(settings, SETTINGS.assessmentScorecard, 0),
                  data: scorecards.data.map((card) => ({ title: get(card, 'name'), value: get(card, 'id') })),
                  props: {
                    label: 'Default Scorecard',
                    disabled: !canEdit,
                  },
                },
                {
                  id: SETTINGS.assessmentColorScale,
                  type: 'ColorSlider',
                  defaultValue: () => get(settings, SETTINGS.assessmentColorScale, [0, 100]),
                  props: {
                    label: 'Color Scale',
                    disabled: !canEdit,
                  },
                },
                {
                  id: SETTINGS.assessmentMetaDefaults,
                  type: 'Checkbox',
                  defaultValue: () => {
                    const val = get(settings, SETTINGS.assessmentMetaDefaults, {});
                    return {
                      tasks_enabled: val.tasks_enabled !== false,
                      targets_enabled: val.targets_enabled !== false,
                    };
                  },
                  options: [
                    { label: 'Create Tasks', value: 'tasks_enabled' },
                    { label: 'Update New Targets', value: 'targets_enabled' },
                  ],
                  props: {
                    label: 'Assessment Defaults',
                    disabled: !canEdit,
                    showAll: true,
                  },
                },
                {
                  id: SETTINGS.customAssessmentsAllowed,
                  type: 'ToggleButton',
                  options: [
                    { title: 'ON', value: '1' },
                    { title: 'OFF', value: '0' },
                  ],
                  defaultValue: () => get(settings, SETTINGS.customAssessmentsAllowed, '1'),
                  props: {
                    exclusive: true,
                    label: 'Custom Assessments Allowed',
                    helperText: 'When OFF, users will only be able to create assessments from templates (unless they have the "Create Assessment Templates" permission)',
                    disabled: !canEdit,
                  },
                },
                {
                  id: SETTINGS.assessmentPrintOptions,
                  props: { label: 'Assessment Print Options' },
                  component: () => {
                    const printVal = (() => {
                      const raw = get(settings, SETTINGS.assessmentPrintOptions);
                      if (Array.isArray(raw) && size(raw)) return raw;
                      return undefined;
                    })();
                    return (
                      <PrintOptionsEditor
                        value={get(formState, SETTINGS.assessmentPrintOptions, printVal)}
                        onChange={(val) => {
                          set(formState, SETTINGS.assessmentPrintOptions, val);
                          setFormState({ ...formState });
                        }}
                        disabled={!canEdit}
                      />
                    );
                  },
                },
              ],
            },
            {
              title: 'Modes',
              screenContainerStyle: isMobile ? AS_SCREEN_CONTAINER_MOBILE : AS_SCREEN_CONTAINER,
              screenContainer: {
                component: Paper,
                props: {
                  elevation: 5,
                  className: clsx(AS_SCREEN_WIDTH, AS_ACCESS_CONTAINER),
                },
              },
              inputs: [
                {
                  id: '_qualityMode',
                  type: 'ToggleButton',
                  options: [
                    { title: 'ON', value: '1' },
                    { title: 'OFF', value: '0' },
                  ],
                  defaultValue: () => {
                    const arr = get(settings, SETTINGS.appMode, [APP_MODE_IDS.Quality]);
                    return Array.isArray(arr) && arr.includes(APP_MODE_IDS.Quality) ? '1' : '0';
                  },
                  props: {
                    exclusive: true,
                    label: 'Quality Mode',
                    disabled: !canEdit,
                  },
                },
                {
                  id: '_safetyMode',
                  type: 'ToggleButton',
                  options: [
                    { title: 'ON', value: '1' },
                    { title: 'OFF', value: '0' },
                  ],
                  defaultValue: () => {
                    const arr = get(settings, SETTINGS.appMode, [APP_MODE_IDS.Quality]);
                    return Array.isArray(arr) && arr.includes(APP_MODE_IDS.Safety) ? '1' : '0';
                  },
                  props: {
                    exclusive: true,
                    label: 'Safety Mode',
                    disabled: !canEdit,
                  },
                },
                {
                  component: () => {
                    const qVal = get(formState, '_qualityMode');
                    const sVal = get(formState, '_safetyMode');
                    const arr = get(settings, SETTINGS.appMode, [APP_MODE_IDS.Quality]);
                    const safeArr = Array.isArray(arr) ? arr : [APP_MODE_IDS.Quality];
                    const qualityOn = String(qVal !== undefined ? qVal : safeArr.includes(APP_MODE_IDS.Quality) ? '1' : '0') === '1';
                    const safetyOn = String(sVal !== undefined ? sVal : safeArr.includes(APP_MODE_IDS.Safety) ? '1' : '0') === '1';
                    if (!qualityOn && !safetyOn) {
                      return <FormHelperText error>At least one mode must be turned on.</FormHelperText>;
                    }
                    return null;
                  },
                },
              ],
            },
          ],
          [
            {
              title: 'Locations',
              screenContainerStyle: isMobile ? AS_SCREEN_CONTAINER_MOBILE : AS_SCREEN_CONTAINER,
              screenContainer: {
                component: Paper,
                props: {
                  elevation: 5,
                  className: AS_ACCESS_CONTAINER,
                },
              },
              inputs: [{ component: renderLocations() }],
            },
            {
              title: 'User Groups',
              screenContainerStyle: isMobile ? AS_SCREEN_CONTAINER_MOBILE : AS_SCREEN_CONTAINER,
              screenContainer: {
                component: Paper,
                props: {
                  elevation: 5,
                  className: AS_ACCESS_CONTAINER,
                },
              },
              inputs: [{ component: renderUserGroups() }],
            },
            {
              title: 'Sharing',
              screenContainerStyle: isMobile ? AS_SCREEN_CONTAINER_MOBILE : AS_SCREEN_CONTAINER,
              screenContainer: {
                component: Paper,
                props: {
                  elevation: 5,
                  className: clsx(AS_ACCESS_CONTAINER),
                },
              },
              inputs: [
                {
                  // PFM-2646: synthetic id so the generic per-setting save loop
                  // never POSTs this under a backend name; the combine-write
                  // block below merges it into the single Link Sharing blob.
                  id: '_shareAllowMode',
                  type: 'RadioButton',
                  defaultValue: () => shareAllowModeDefault(),
                  options: SHARE_LINK_ALLOW_MODES.map((m) => ({ value: m.value, label: m.title })),
                  props: {
                    label: 'Allow Link Sharing',
                    helperText: 'Live links can be accessed by anyone, even outside of your organization. If turned off, sharing will generate links that only users in your organization can access.',
                    disabled: !canEdit,
                  },
                },
                {
                  id: '_shareExpirationDays',
                  type: 'TextField',
                  defaultValue: () => shareExpirationDaysDefault(),
                  props: {
                    label: 'Days Link Active',
                    type: 'number',
                    disabled: !canEdit,
                    inputProps: { min: 1, max: 30 },
                    helperText: 'Number of days a live link stays valid (1–30).',
                  },
                },
                // PFM-2674: "Allowed Email Domains" hidden for now — the share
                // workflow doesn't enforce domain restrictions yet (links can be
                // copied/forwarded to anyone), so the field only causes confusion.
                // Save logic below preserves any existing allowed_domains value
                // by falling back to the stored blob when the form value is absent.
              ],
            },
            {
              title: 'Self Registration',
              screenContainerStyle: isMobile ? AS_SCREEN_CONTAINER_MOBILE : AS_SCREEN_CONTAINER,
              screenContainer: {
                component: Paper,
                props: {
                  elevation: 5,
                  className: clsx(AS_ACCESS_CONTAINER),
                },
              },
              inputs: [
                {
                  id: SETTINGS.selfRegistration,
                  type: 'RadioButton',
                  defaultValue: () => get(settings, SETTINGS.selfRegistration),
                  options: [
                    { value: '1', label: 'Enabled' },
                    { value: '0', label: 'Disabled' },
                  ],
                  props: {
                    label: 'Self-Registration Status',
                    disabled: !canEdit,
                    className: AS_FLEX_ROW_CLASS,
                  },
                },
                {
                  id: SETTINGS.allowedDomains,
                  type: 'ChipAdd',
                  defaultValue: () => [...get(settings, SETTINGS.allowedDomains, [])],
                  props: {
                    label: 'Allowed Domains',
                    disabled: !canEdit,
                  },
                },
                {
                  component: (
                    <FullWidth>
                      <InputLabel shrink={true} variant="standard" disableAnimation={true}>
                        Access Restrictions
                      </InputLabel>
                      <Droprealms
                        canBackOutOfBuilder={false}
                        alwaysShow
                        showModify={canEdit}
                        onUpdate={onUpdateAccess}
                        realms={accessRestrictions}
                        title={size(accessRestrictions) === 0 ? 'None' : undefined}
                      />
                    </FullWidth>
                  ),
                  props: {
                    disabled: !canEdit,
                  },
                },
                {
                  id: SETTINGS.defaultPermissions,
                  type: 'Checkbox',
                  defaultValue: () => ac,
                  options: filterPermissionsByNav().map((up) => ({ ...up, label: up.title })),
                  props: {
                    label: 'Default Permissions',
                    disabled: !canEdit,
                  },
                },
                {
                  component: () => {
                    const show = get(formState, SETTINGS.selfRegistration) !== '0';
                    const link = `${window.location.origin}/register/${get(userRealm, 'id')}`;

                    return !show ? null : (
                      <FormHelperText>
                        <Box className="boxContainer">
                          <Box>{'New or existing users can use these links to configure their account. There are three different tiers for self-registration:'}</Box>
                          <Box style={{ height: 10 }} />
                          <Box>
                            {`Corporate users: `}
                            <a target={'_blank'} rel={'noopener noreferrer'} href={`${link}/builder`}>{`${link}/builder`}</a>
                          </Box>
                          <Box>
                            {`Divisional users: `}
                            <a target={'_blank'} rel={'noopener noreferrer'} href={`${link}/division`}>{`${link}/division`}</a>
                          </Box>
                        </Box>
                      </FormHelperText>
                    );
                  },
                },
              ],
            },
          ],
        ]}
      />

      <FullScreen
        centerContent
        onClose={toggleAccess}
        title={
          <>
            <Icon style={{ marginRight: 5 }}>location_on</Icon> Manage Locations
          </>
        }
        open={accessModal}
      >
        <Access canAdd={true} tiersCanAdd={['builder', 'division', 'community']} deselectOnChange={true} realms={[]} showOnlyUsersBuilder _allRealms={allRealms} editable={true} showKey />
      </FullScreen>

      <UserGroupModal {...ugmodal} />

      <ConfirmModal {...conf} />
    </AccountSettingsRoot>
  );
});
